/* ⚠️ GENERATED from app/services/ghostRows.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/ghostRows.ts — the one definition of a row that must never be a bubble.
//
// THE REPORT
//   "a member adds a reaction while my phone is in the background; I open the app
//   and the chats list says no message yet, I enter the chat and there is an empty
//   bubble under the NEW MESSAGE divider."
//
// 🟥 A REACTION IS NOT A MESSAGE, AND THE SERVER SAYS SO IN A FIELD IT IS ALLOWED
//    TO DROP. The push for a reaction carries msgType:'system' — because the
//    sender stamped systemType:'reaction', and the builder turns any systemType
//    into msgType:'system' — and the WHICH system event it is lives only in
//    `systemType`. Two server paths then part the two apart:
//
//      • shrinkDataForFcm's Pass 2a deletes any field that is not PROTECTED when
//        the payload is over budget. `msgType` is protected. `systemType` was not.
//      • the "payload too big" retry rebuilds a minimal payload by hand. It
//        copies msgType. It did not copy systemType.
//
//    Either way the background handler receives {msgType:'system'} with nothing
//    saying what the system event was. Its reaction guard reads `systemType`, so
//    the guard cannot fire, and the row is cached as type:'system' with no
//    systemType — a bubble the system renderer has nothing to draw for (the empty
//    rectangle in the screenshot) and that every preview helper renders as the
//    empty string (the "no message yet" row above it). One missing field, both
//    symptoms.
//
// 🟥 AND IT IS THE GROUP, as reported, for a reason that is not a coincidence: a
//    group push carries groupName, groupImage and the participant images on top
//    of everything a 1-on-1 push carries, so a group is where the budget is
//    actually reached and where those two paths actually run.
//
// 🟢 SO THE SERVER KEEPS THE FIELD (both paths) AND NO READER TRUSTS IT ANYWAY.
//    A row that cannot be drawn is not written, and one already on disk from
//    before this fix is dropped on sight — the ghosts a user already has heal on
//    the next read instead of waiting for a migration they have already run.
//
// 🟢 ONE PREDICATE, NOT SEVEN COPIES OF IT. This rule was spelled out at six
//    call sites (two in ChatModal, one in chatOpenLoad, one in MessageDB's
//    purge, one in unreadRules, one in the web client) and every copy listed a
//    slightly different set — which is why `type:'system'` with no systemType
//    slipped through all of them.

/** A system row's `systemType` when the row is machinery, not a notice. */
const EVENT_SYSTEM_TYPES = ['reaction', 'vote'];

/** Values that mean "this field was never really set". */
const BLANK = ['', 'null', 'undefined', 'text', 'false'];
const blank = v => v == null || BLANK.includes(String(v));

/**
 * Is this row machinery that must never occupy a line in a chat, a preview, or
 * an unread count?
 *
 * 🟢 Deliberately total: every caller asks this ONE question, so a new kind of
 *    machinery is excluded everywhere by editing this function.
 */
export const isGhostRow = m => {
  if (!m) return true;
  const type = String(m.type || '');
  const sys = m.systemType;

  /* the explicit action documents — a reaction or a poll vote written into the
     messages collection so an offline device can replay the intent */
  if (type === 'reaction_action' || type === 'vote_action') return true;

  /* a delete command is an instruction about a message, not a message */
  if (String(sys || '') === 'delete_command') return true;
  if (type === 'system') {
    /* 🟥 THE REPORTED ONE. A system row whose systemType did not survive the
       wire has no notice to render and no preview to contribute; it is a blank
       bubble and a blank chats-list line. Before this it was written. */
    if (blank(sys)) return true;
    /* a reaction or a vote that DID keep its systemType — the same machinery,
       just correctly labelled. Never a bubble either. */
    if (EVENT_SYSTEM_TYPES.includes(String(sys))) return true;
  }

  /* 🟥 THE EMPTY ALBUM IS DELIBERATELY NOT HERE. `purgeGhostMessages` deletes a
     `type:'images'` row with no `images` array, and that is sound as a ONE-TIME
     sweep of legacy JSON — but this predicate now runs on every read, including
     on an album of this user's own that is still uploading and may be holding
     its pictures under a different field for a moment. Hiding a message that is
     merely mid-flight is a worse bug than the one being fixed, so the album
     clause stays where it was, with the migration that has evidence for it. */

  return false;
};

/**
 * Would caching this incoming push produce a ghost? Asked by the receive paths
 * BEFORE they write, which is the only place the ghost can still be prevented
 * rather than cleaned up.
 *
 * 🟥 IT IS THE SAME PREDICATE, AND THAT IS A CONSTRAINT ON `isGhostRow`, NOT A
 *    COINCIDENCE. A push is not a row: an album's push carries `images` as a
 *    JSON string, or has it dropped by the shrink guard and re-fetches on open,
 *    so an empty-album clause inside `isGhostRow` would read every photo-batch
 *    push as a ghost and throw the batch away. I wrote this as a second,
 *    deliberately narrower function for exactly that reason — and then moved
 *    the album clause out of `isGhostRow` (where it could not survive a live
 *    read anyway), which left the two bodies identical. Two identical
 *    predicates are how six slightly different copies of a rule start, so this
 *    is a named call through to the one, and `isGhostRow` must stay answerable
 *    about a push: no clause in it may depend on a field only a stored row has.
 */
export const pushWouldGhost = (msgType, systemType) => isGhostRow({
  type: String(msgType || ''),
  systemType
});

/**
 * A reaction that lost its label but kept its substance.
 *
 * 🟢 THE PAYLOAD IS STILL USABLE. emoji + targetMessageId are what applying a
 *    reaction needs, and they are ordinary droppable fields — but when they DID
 *    survive and only `systemType` did not, the event is recoverable and must be
 *    applied rather than merely refused. This is what keeps the fix from turning
 *    a wrong bubble into a missing reaction.
 */
export const looksLikeReaction = data => {
  if (!data) return false;
  if (String(data.systemType || '') === 'reaction') return true;
  if (String(data.msgType || data.type || '') === 'reaction_action') return true;
  return !!(data.emoji && data.targetMessageId);
};

/**
 * Hide every undrawable row in a loaded list.
 *
 * 🟢 THE SAME CHOKEPOINT AS THE DELETION FILTER, AND FOR THE SAME REASON. The
 *    ghosts a device is already carrying must stop being drawn without waiting
 *    for a migration the user has already run, so the one place every reader
 *    passes through does it.
 *
 * 🟢 READ-SIDE ONLY, NO DELETE. Hiding a row cannot lose a message; deleting one
 *    can, and `purgeGhostMessages` already owns that decision — through this same
 *    predicate, so the two can never disagree about what a ghost is.
 *
 * 🟢 Returns the SAME array reference when there is nothing to drop, which is the
 *    overwhelmingly common case: a loaded chat pays one pass and no allocation.
 *
 * 🟥 IT LIVES HERE RATHER THAN IN MessageDB BECAUSE IT HAS TO BE PROVABLE. In
 *    MessageDB it sat behind Firestore and expo-sqlite imports, so no test could
 *    call it — and a mutation that replaced its body with `return messages` was
 *    invisible to a suite that could only check the name existed and the call
 *    sites were there.
 */
export const dropGhostRows = messages => {
  if (!Array.isArray(messages)) return messages;
  let bad = 0;
  for (const m of messages) if (m && isGhostRow(m)) bad += 1;
  if (bad === 0) return messages;
  console.warn(`🧹 [ghost] hid ${bad} undrawable row(s) on read`);
  return messages.filter(m => !(m && isGhostRow(m)));
};
export default {
  isGhostRow,
  dropGhostRows,
  pushWouldGhost,
  looksLikeReaction
};
